import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useBranding } from "../lib/branding";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Buildings, Storefront, Users as UsersIcon, UserCircle, Image as ImageIcon, XCircle, Wrench, Trash, Plus } from "@phosphor-icons/react";
import UsersPage from "./Users";
import SettingsApertura from "./SettingsApertura";
import BrandPicker from "../components/BrandPicker";
import { Switch } from "../components/ui/switch";

const ACCENT_PRESETS = [
    { label: "Rosso segnale", value: "#FF2A00" },
    { label: "Verde", value: "#00C853" },
    { label: "Blu", value: "#2962FF" },
    { label: "Viola", value: "#7C3AED" },
    { label: "Ocra", value: "#D97706" },
    { label: "Nero", value: "#09090b" },
];

function readFileAsBase64(file, maxDim = 512) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                let { width, height } = img;
                if (width > maxDim || height > maxDim) {
                    const ratio = Math.min(maxDim / width, maxDim / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/png", 0.92));
            };
            img.onerror = reject;
            img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function TabButton({ active, onClick, icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${active ? "border-ink text-ink" : "border-transparent text-zinc-500 hover:text-ink"}`}
            data-testid={testid}
        >
            {icon}
            {label}
        </button>
    );
}

function CompanyTab() {
    const { company, reload } = useBranding();
    const [form, setForm] = useState({
        name: "",
        piva: "",
        accent_color: "#FF2A00",
        logo_b64: null,
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (company) {
            setForm({
                name: company.name || "",
                piva: company.piva || "",
                accent_color: company.accent_color || "#FF2A00",
                logo_b64: company.logo_b64 || null,
            });
        }
    }, [company]);

    const onLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Immagine troppo grande (max 2MB)");
            return;
        }
        try {
            const b64 = await readFileAsBase64(file);
            setForm({ ...form, logo_b64: b64 });
        } catch {
            toast.error("Impossibile leggere il file");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/companies/me", {
                name: form.name,
                piva: form.piva,
                accent_color: form.accent_color,
                logo_b64: form.logo_b64 || "",
            });
            toast.success("Azienda aggiornata");
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-zinc-200 p-8 max-w-3xl">
            <div className="overline mb-2">Dati azienda</div>
            <div className="text-xs text-zinc-500 mb-6 max-w-xl">
                Ragione sociale e P.IVA sono dati fiscali (usati nei PDF di chiusura).
                Logo e colore accent rappresentano il tuo brand aziendale all&apos;interno
                dell&apos;app — appaiono accanto al logo della piattaforma.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                    <label className="block mb-4">
                        <div className="overline mb-2">Ragione sociale</div>
                        <input
                            className="inp"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Es. Mia Azienda SRL"
                            data-testid="company-name"
                        />
                    </label>
                    <label className="block mb-4">
                        <div className="overline mb-2">Partita IVA</div>
                        <input
                            className="inp"
                            value={form.piva}
                            onChange={(e) => setForm({ ...form, piva: e.target.value })}
                            placeholder="12345678901"
                            data-testid="company-piva"
                        />
                    </label>
                </div>
                <div>
                    <div className="overline mb-2">Logo azienda</div>
                    <div className="border border-dashed border-zinc-300 p-4 mb-2 flex items-center justify-center bg-zinc-50 min-h-[160px]">
                        {form.logo_b64 ? (
                            <div className="relative">
                                <img
                                    src={form.logo_b64}
                                    alt="Logo"
                                    className="max-h-32 max-w-full"
                                    data-testid="company-logo-preview"
                                />
                                <button
                                    onClick={() => setForm({ ...form, logo_b64: null })}
                                    className="absolute -top-2 -right-2 bg-white border border-zinc-300 rounded-full p-1 hover:border-red-500"
                                    data-testid="company-logo-remove"
                                    title="Rimuovi logo"
                                >
                                    <XCircle size={16} weight="bold" className="text-red-600" />
                                </button>
                            </div>
                        ) : (
                            <div className="text-zinc-400 text-sm flex flex-col items-center gap-2">
                                <ImageIcon size={32} weight="thin" />
                                Nessun logo
                            </div>
                        )}
                    </div>
                    <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            className="hidden"
                            onChange={onLogoChange}
                            data-testid="company-logo-input"
                        />
                        Carica immagine
                    </label>
                    <div className="text-xs text-zinc-500 mt-1">
                        PNG/JPG/WebP. Verrà ridimensionato a max 512px.
                    </div>
                </div>
            </div>

            <div className="mb-8">
                <div className="overline mb-3">Colore accent</div>
                <div className="flex flex-wrap gap-2">
                    {ACCENT_PRESETS.map((c) => (
                        <button
                            key={c.value}
                            onClick={() => setForm({ ...form, accent_color: c.value })}
                            className={`flex items-center gap-2 px-3 py-2 border text-xs font-bold ${form.accent_color === c.value ? "border-ink" : "border-zinc-200 hover:border-zinc-400"}`}
                            data-testid={`accent-${c.value}`}
                        >
                            <div
                                className="w-4 h-4 border border-ink"
                                style={{ background: c.value }}
                            />
                            {c.label}
                        </button>
                    ))}
                </div>
            </div>

            <button
                onClick={save}
                disabled={saving}
                className="btn-primary"
                data-testid="company-save"
            >
                {saving ? "Salvataggio…" : "Salva modifiche"}
            </button>
        </div>
    );
}

function AgencyTab() {
    const { agencyId } = useAuth();
    const [ag, setAg] = useState(null);
    const [form, setForm] = useState({ name: "", address: "", phone: "", logo_b64: null, brand_concessionario: null });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const r = await api.get("/agencies");
        const found = r.data.find((a) => a.id === agencyId);
        setAg(found || null);
        if (found) {
            setForm({
                name: found.name || "",
                address: found.address || "",
                phone: found.phone || "",
                logo_b64: found.logo_b64 || null,
                brand_concessionario: found.brand_concessionario || null,
            });
        }
    };
    useEffect(() => {
        if (agencyId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const onLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Immagine troppo grande (max 2MB)");
            return;
        }
        try {
            const b64 = await readFileAsBase64(file);
            setForm({ ...form, logo_b64: b64 });
        } catch {
            toast.error("Impossibile leggere il file");
        }
    };

    const save = async () => {
        if (!ag) return;
        setSaving(true);
        try {
            await api.patch(`/agencies/${ag.id}`, {
                name: form.name,
                address: form.address,
                phone: form.phone,
                logo_b64: form.logo_b64 || "",
                brand_concessionario: form.brand_concessionario || "",
            });
            toast.success("Agenzia aggiornata");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!ag) return <div className="overline p-6">Nessuna agenzia selezionata</div>;

    return (
        <div className="bg-white border border-zinc-200 p-8 max-w-3xl">
            <div className="overline mb-6">Sede: {ag.name}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                    <label className="block mb-4">
                        <div className="overline mb-2">Nome sede</div>
                        <input
                            className="inp"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="agency-name"
                        />
                    </label>
                    <label className="block mb-4">
                        <div className="overline mb-2">Indirizzo</div>
                        <input
                            className="inp"
                            value={form.address}
                            onChange={(e) =>
                                setForm({ ...form, address: e.target.value })
                            }
                            placeholder="Via Roma 10, Milano"
                            data-testid="agency-address"
                        />
                    </label>
                    <label className="block mb-4">
                        <div className="overline mb-2">Telefono</div>
                        <input
                            className="inp"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="+39 02 1234567"
                            data-testid="agency-phone"
                        />
                    </label>
                    <label className="block mb-4">
                        <div className="overline mb-2">Brand concessionario</div>
                        <BrandPicker
                            value={form.brand_concessionario}
                            onChange={(id) =>
                                setForm({
                                    ...form,
                                    brand_concessionario: id === "custom" ? null : id,
                                })
                            }
                            testId="agency-brand"
                        />
                        <div className="text-xs text-zinc-500 mt-1">
                            Identifica la rete (SNAI, Sisal, ecc.). Mostra il logo del
                            brand nello switcher e nelle stampe.
                        </div>
                    </label>
                </div>
                <div>
                    <div className="overline mb-2">Logo sede (opzionale)</div>
                    <div className="border border-dashed border-zinc-300 p-4 mb-2 flex items-center justify-center bg-zinc-50 min-h-[160px]">
                        {form.logo_b64 ? (
                            <div className="relative">
                                <img
                                    src={form.logo_b64}
                                    alt="Logo sede"
                                    className="max-h-32 max-w-full"
                                    data-testid="agency-logo-preview"
                                />
                                <button
                                    onClick={() => setForm({ ...form, logo_b64: null })}
                                    className="absolute -top-2 -right-2 bg-white border border-zinc-300 rounded-full p-1 hover:border-red-500"
                                >
                                    <XCircle size={16} weight="bold" className="text-red-600" />
                                </button>
                            </div>
                        ) : (
                            <div className="text-zinc-400 text-sm flex flex-col items-center gap-2">
                                <ImageIcon size={32} weight="thin" />
                                Usa il logo azienda
                            </div>
                        )}
                    </div>
                    <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            className="hidden"
                            onChange={onLogoChange}
                            data-testid="agency-logo-input"
                        />
                        Carica immagine
                    </label>
                    <div className="text-xs text-zinc-500 mt-1">
                        Se presente, sostituisce il logo azienda sui documenti di questa sede.
                    </div>
                </div>
            </div>
            <button
                onClick={save}
                disabled={saving}
                className="btn-primary"
                data-testid="agency-save"
            >
                {saving ? "Salvataggio…" : "Salva modifiche"}
            </button>

            <LocationSection agencyId={ag.id} ag={ag} onSaved={load} />
            <POSDevicesSection agencyId={ag.id} />
            <ShiftPresetsSection agencyId={ag.id} />
        </div>
    );
}

const COORD_RE = /^-?\d+([.,]\d+)?$/;

function parseStrictCoord(raw) {
    const trimmed = String(raw).trim();
    if (!COORD_RE.test(trimmed)) return NaN;
    return parseFloat(trimmed.replace(",", "."));
}

function haversineM(lat1, lng1, lat2, lng2) {
    const R = 6_371_000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dPhi = toRad(lat2 - lat1);
    const dLambda = toRad(lng2 - lng1);
    const a =
        Math.sin(dPhi / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLambda / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
}

function LocationSection({ agencyId, ag, onSaved }) {
    const [lat, setLat] = useState(ag?.location_lat ?? "");
    const [lng, setLng] = useState(ag?.location_lng ?? "");
    const [radius, setRadius] = useState(ag?.geofence_radius_m ?? 250);
    const [locating, setLocating] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLat(ag?.location_lat ?? "");
        setLng(ag?.location_lng ?? "");
        setRadius(ag?.geofence_radius_m ?? 250);
        setTestResult(null);
    }, [ag?.id, ag?.location_lat, ag?.location_lng, ag?.geofence_radius_m]);

    const useMyLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocalizzazione non supportata da questo dispositivo/browser");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLat(pos.coords.latitude.toFixed(6));
                setLng(pos.coords.longitude.toFixed(6));
                setLocating(false);
                setTestResult(null);
                toast.success("Posizione rilevata. Ricordati di salvare.");
            },
            (err) => {
                setLocating(false);
                toast.error("Impossibile rilevare la posizione: " + err.message);
            },
            { enableHighAccuracy: true, timeout: 10000 },
        );
    };

    const testHere = () => {
        const latNum = parseStrictCoord(lat);
        const lngNum = parseStrictCoord(lng);
        if (isNaN(latNum) || isNaN(lngNum)) {
            toast.error("Inserisci prima delle coordinate valide (anche non salvate) per poterle testare");
            return;
        }
        if (!navigator.geolocation) {
            toast.error("Geolocalizzazione non supportata da questo dispositivo/browser");
            return;
        }
        setTesting(true);
        setTestResult(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const dist = haversineM(latNum, lngNum, pos.coords.latitude, pos.coords.longitude);
                setTestResult({ distance: dist, accuracy: pos.coords.accuracy });
                setTesting(false);
            },
            (err) => {
                setTesting(false);
                toast.error("Impossibile rilevare la posizione: " + err.message);
            },
            { enableHighAccuracy: true, timeout: 10000 },
        );
    };

    const save = async () => {
        const latNum = lat === "" ? null : parseStrictCoord(lat);
        const lngNum = lng === "" ? null : parseStrictCoord(lng);
        if ((lat !== "" && isNaN(latNum)) || (lng !== "" && isNaN(lngNum))) {
            toast.error("Latitudine/longitudine non valide");
            return;
        }
        if ((latNum === null) !== (lngNum === null)) {
            toast.error("Inserisci sia latitudine che longitudine, o lasciale entrambe vuote");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/agencies/${agencyId}`, {
                location_lat: latNum,
                location_lng: lngNum,
                geofence_radius_m: parseInt(radius, 10) || 250,
            });
            toast.success(latNum === null ? "Posizione sede rimossa" : "Posizione sede aggiornata");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const clearLocation = async () => {
        if (!window.confirm("Rimuovere la posizione della sede? Gli operatori potranno aprire il turno da qualsiasi luogo finché non la reimposti.")) return;
        setLat("");
        setLng("");
        setSaving(true);
        try {
            await api.patch(`/agencies/${agencyId}`, {
                location_lat: null,
                location_lng: null,
            });
            toast.success("Posizione sede rimossa");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const hasLocation = ag?.location_lat != null && ag?.location_lng != null;
    const geofenceEnabled = ag?.geofence_enabled !== false;
    const [togglingGeofence, setTogglingGeofence] = useState(false);

    const toggleGeofence = async (checked) => {
        setTogglingGeofence(true);
        try {
            await api.patch(`/agencies/${agencyId}`, { geofence_enabled: checked });
            toast.success(checked ? "Controllo posizione riattivato" : "Controllo posizione sospeso (coordinate mantenute)");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setTogglingGeofence(false);
        }
    };

    return (
        <div className="mt-10 pt-8 border-t border-zinc-200" data-testid="location-section">
            <div className="mb-4">
                <h3 className="text-xl font-black tracking-tighter mb-1">Posizione sede &amp; badging</h3>
                <p className="text-sm text-zinc-500 max-w-xl">
                    Se imposti le coordinate della sala, gli operatori potranno aprire il turno
                    (apertura cassa) solo trovandosi entro il raggio scelto. Il controllo usa la
                    posizione GPS del dispositivo e si applica <strong>solo da smartphone</strong> —
                    da PC/tablet il turno si apre sempre, perché la geolocalizzazione di rete può
                    sbagliare di chilometri (reti non mappate, operatori mobile che risolvono sulla
                    città). Il WiFi della sala non è leggibile da un&apos;app web per motivi di
                    sicurezza del browser.
                </p>
                <div
                    className={`mt-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-2 py-1 ${
                        hasLocation && geofenceEnabled
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : hasLocation
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-zinc-50 text-zinc-500 border border-zinc-200"
                    }`}
                    data-testid="location-status"
                >
                    {hasLocation
                        ? geofenceEnabled
                            ? "Controllo posizione attivo"
                            : "Controllo posizione sospeso (coordinate salvate)"
                        : "Controllo posizione disattivato"}
                </div>
            </div>
            {hasLocation && (
                <label className="flex items-center gap-3 mb-5 cursor-pointer" data-testid="location-toggle-row">
                    <Switch
                        checked={geofenceEnabled}
                        disabled={togglingGeofence}
                        onCheckedChange={toggleGeofence}
                        data-testid="location-geofence-toggle"
                    />
                    <span className="text-sm font-bold">
                        Controllo posizione attivo
                        <span className="block text-xs font-normal text-zinc-500">
                            Disattivalo per sospendere il blocco senza perdere le coordinate salvate — riattivalo quando vuoi senza rifare il posizionamento.
                        </span>
                    </span>
                </label>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <label className="block">
                    <div className="overline mb-2">Latitudine</div>
                    <input
                        className="inp tabular-nums"
                        value={lat}
                        onChange={(e) => {
                            setLat(e.target.value);
                            setTestResult(null);
                        }}
                        placeholder="Es. 45.4642"
                        data-testid="location-lat"
                    />
                </label>
                <label className="block">
                    <div className="overline mb-2">Longitudine</div>
                    <input
                        className="inp tabular-nums"
                        value={lng}
                        onChange={(e) => {
                            setLng(e.target.value);
                            setTestResult(null);
                        }}
                        placeholder="Es. 9.1900"
                        data-testid="location-lng"
                    />
                </label>
                <label className="block">
                    <div className="overline mb-2">Raggio tolleranza</div>
                    <select
                        className="inp"
                        value={radius}
                        onChange={(e) => setRadius(e.target.value)}
                        data-testid="location-radius"
                    >
                        <option value={100}>100 metri</option>
                        <option value={250}>250 metri</option>
                        <option value={500}>500 metri</option>
                    </select>
                </label>
            </div>
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={useMyLocation}
                    disabled={locating}
                    className="btn-secondary"
                    data-testid="location-use-current"
                >
                    {locating ? "Rilevamento…" : "Usa posizione corrente"}
                </button>
                {hasLocation && (
                    <button
                        type="button"
                        onClick={clearLocation}
                        disabled={saving}
                        className="btn-destructive"
                        data-testid="location-clear"
                    >
                        Rimuovi posizione
                    </button>
                )}
                <button
                    type="button"
                    onClick={testHere}
                    disabled={testing}
                    className="btn-secondary"
                    data-testid="location-test-here"
                    title="Stando fisicamente in sede, verifica subito quanti metri calcola il sistema rispetto alle coordinate sopra"
                >
                    {testing ? "Test in corso…" : "Testa qui"}
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="btn-primary"
                    data-testid="location-save"
                >
                    {saving ? "Salvataggio…" : "Salva posizione"}
                </button>
            </div>
            {testResult && (
                <div
                    className={`mt-4 p-3 border text-sm font-bold ${
                        testResult.distance <= radius
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                    data-testid="location-test-result"
                >
                    {testResult.distance <= radius ? "Dentro il raggio: " : "Fuori dal raggio: "}
                    ~{Math.round(testResult.distance)}m dalle coordinate inserite
                    {testResult.accuracy ? ` (precisione dichiarata dal device: ±${Math.round(testResult.accuracy)}m)` : ""}.
                </div>
            )}
        </div>
    );
}

function ShiftPresetsSection({ agencyId }) {
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/agencies/${agencyId}/shift-presets`)
            .then((r) => setPresets(r.data.presets || []))
            .finally(() => setLoading(false));
    };

    useEffect(load, [agencyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const update = (idx, field, val) => {
        const next = [...presets];
        next[idx] = { ...next[idx], [field]: val };
        setPresets(next);
    };
    const remove = (idx) => setPresets(presets.filter((_, i) => i !== idx));
    const add = () => {
        if (presets.length >= 12) {
            toast.error("Massimo 12 preset");
            return;
        }
        setPresets([
            ...presets,
            { label: "Nuovo turno", start_time: "09:00", end_time: "13:00" },
        ]);
    };

    const save = async () => {
        setSaving(true);
        try {
            // Validazione: tutti i campi compilati
            for (const p of presets) {
                if (!p.label?.trim()) {
                    toast.error("Tutti i preset devono avere un nome");
                    setSaving(false);
                    return;
                }
                if (!/^\d{2}:\d{2}$/.test(p.start_time) || !/^\d{2}:\d{2}$/.test(p.end_time)) {
                    toast.error(`Orari non validi per "${p.label}"`);
                    setSaving(false);
                    return;
                }
            }
            await api.patch(`/agencies/${agencyId}/shift-presets`, { presets });
            toast.success("Preset salvati");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <div className="mt-12 pt-8 border-t border-zinc-200" data-testid="shift-presets-section">
            <div className="mb-4">
                <h3 className="text-xl font-black tracking-tighter mb-1">Preset turni</h3>
                <p className="text-sm text-zinc-500">
                    Modelli orario rapidi che compaiono come pulsanti nel calendario quando crei un nuovo
                    turno (es. Mattino 08–14, Pomeriggio 14–22). Massimo 12 preset.
                </p>
            </div>
            <div className="space-y-2 mb-4">
                {presets.map((p, idx) => (
                    <div
                        key={idx}
                        className="grid grid-cols-12 gap-2 items-center border border-zinc-200 p-3"
                        data-testid={`preset-row-${idx}`}
                    >
                        <input
                            className="inp col-span-5"
                            value={p.label}
                            onChange={(e) => update(idx, "label", e.target.value)}
                            placeholder="Nome (es. Mattino)"
                            data-testid={`preset-label-${idx}`}
                        />
                        <input
                            type="time"
                            className="inp col-span-3 tabular-nums"
                            value={p.start_time}
                            onChange={(e) => update(idx, "start_time", e.target.value)}
                            data-testid={`preset-start-${idx}`}
                        />
                        <input
                            type="time"
                            className="inp col-span-3 tabular-nums"
                            value={p.end_time}
                            onChange={(e) => update(idx, "end_time", e.target.value)}
                            data-testid={`preset-end-${idx}`}
                        />
                        <button
                            type="button"
                            onClick={() => remove(idx)}
                            className="col-span-1 text-zinc-400 hover:text-red-600 flex justify-center"
                            aria-label="Rimuovi"
                            data-testid={`preset-remove-${idx}`}
                        >
                            <Trash size={14} weight="bold" />
                        </button>
                    </div>
                ))}
                {presets.length === 0 && (
                    <div className="text-xs text-zinc-500 p-3 bg-zinc-50">
                        Nessun preset. Verranno usati i 4 di default (Mattino, Pomeriggio, Mezza mattina, Mezzo pomeriggio).
                    </div>
                )}
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={add}
                    className="btn-secondary inline-flex items-center gap-1.5"
                    data-testid="preset-add"
                >
                    <Plus size={12} weight="bold" /> Aggiungi preset
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="btn-primary"
                    data-testid="preset-save"
                >
                    {saving ? "Salvataggio…" : "Salva preset"}
                </button>
            </div>
        </div>
    );
}

function POSDevicesSection({ agencyId }) {
    const [devices, setDevices] = useState([]);
    const [wallets, setWallets] = useState([]);
    const [editing, setEditing] = useState(null); // null | {id?, name, wallet_id, fee_percentage}

    const load = async () => {
        const [d, w] = await Promise.all([
            api.get("/pos-devices", { params: { agency_id: agencyId } }),
            api.get("/wallets", { params: { agency_id: agencyId } }),
        ]);
        setDevices(d.data);
        setWallets(w.data.filter((x) => x.type === "banca" || x.type === "cassa"));
    };
    useEffect(() => {
        if (agencyId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const save = async () => {
        if (!editing.name?.trim()) {
            toast.error("Nome obbligatorio");
            return;
        }
        if (!editing.wallet_id) {
            toast.error("Wallet di accredito obbligatorio");
            return;
        }
        const fee = parseFloat(editing.fee_percentage) || 0;
        if (fee < 0 || fee > 100) {
            toast.error("Commissione % non valida");
            return;
        }
        try {
            if (editing.id) {
                await api.patch(`/pos-devices/${editing.id}`, {
                    name: editing.name.trim(),
                    wallet_id: editing.wallet_id,
                    fee_percentage: fee,
                });
                toast.success("POS aggiornato");
            } else {
                await api.post("/pos-devices", {
                    agency_id: agencyId,
                    name: editing.name.trim(),
                    wallet_id: editing.wallet_id,
                    fee_percentage: fee,
                });
                toast.success("POS creato");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (d) => {
        if (!window.confirm(`Eliminare POS "${d.name}"?`)) return;
        try {
            await api.delete(`/pos-devices/${d.id}`);
            toast.success("POS eliminato");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="mt-10 pt-8 border-t border-zinc-200" data-testid="pos-devices-section">
            <div className="flex items-center justify-between mb-2">
                <div className="overline">POS configurati</div>
                {!editing && (
                    <button
                        onClick={() =>
                            setEditing({ name: "", wallet_id: wallets[0]?.id || "", fee_percentage: "0" })
                        }
                        className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-2"
                        data-testid="pos-add-btn"
                    >
                        + Aggiungi POS
                    </button>
                )}
            </div>
            <div className="text-xs text-zinc-500 mb-4 leading-relaxed">
                Configura uno o più POS dell&apos;agenzia. La commissione % viene applicata
                automaticamente al settlement come transazione &quot;uscita&quot; categoria{" "}
                <strong>Commissioni POS</strong>.
            </div>

            {devices.length === 0 && !editing && (
                <div className="p-4 border border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
                    Nessun POS configurato.
                </div>
            )}

            {devices.length > 0 && (
                <div className="border border-zinc-200">
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Banca / Wallet</th>
                                <th className="text-right">Commissione</th>
                                <th className="text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {devices.map((d) => {
                                const w = wallets.find((x) => x.id === d.wallet_id);
                                return (
                                    <tr key={d.id} data-testid={`pos-row-${d.id}`}>
                                        <td className="font-bold">{d.name}</td>
                                        <td>{w?.name || "—"}</td>
                                        <td className="text-right tabular-nums">
                                            {d.fee_percentage.toFixed(2)} %
                                        </td>
                                        <td className="text-right">
                                            <button
                                                onClick={() =>
                                                    setEditing({
                                                        id: d.id,
                                                        name: d.name,
                                                        wallet_id: d.wallet_id,
                                                        fee_percentage: String(d.fee_percentage),
                                                    })
                                                }
                                                className="btn-secondary !py-1 !px-2 text-xs mr-2"
                                                data-testid={`pos-edit-${d.id}`}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                onClick={() => remove(d)}
                                                className="btn-secondary !py-1 !px-2 text-xs text-red-600"
                                                data-testid={`pos-delete-${d.id}`}
                                            >
                                                Elimina
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <div className="mt-4 p-4 border border-zinc-300 bg-zinc-50" data-testid="pos-form">
                    <div className="overline mb-3">{editing.id ? "Modifica POS" : "Nuovo POS"}</div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                        <div className="md:col-span-4">
                            <label className="overline mb-1 block">Nome</label>
                            <input
                                className="inp"
                                autoFocus
                                value={editing.name}
                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                placeholder="Es. Nexi, SumUp, POS Mobile"
                                data-testid="pos-form-name"
                            />
                        </div>
                        <div className="md:col-span-4">
                            <label className="overline mb-1 block">Wallet di accredito</label>
                            <select
                                className="inp"
                                value={editing.wallet_id}
                                onChange={(e) => setEditing({ ...editing, wallet_id: e.target.value })}
                                data-testid="pos-form-wallet"
                            >
                                <option value="">Seleziona…</option>
                                {wallets.map((w) => (
                                    <option key={w.id} value={w.id}>
                                        {w.name} ({w.type})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline mb-1 block">Commissione %</label>
                            <input
                                type="number"
                                className="inp tabular-nums text-right"
                                step="0.01"
                                min="0"
                                max="100"
                                value={editing.fee_percentage}
                                onChange={(e) =>
                                    setEditing({ ...editing, fee_percentage: e.target.value })
                                }
                                data-testid="pos-form-fee"
                            />
                        </div>
                        <div className="md:col-span-2 flex gap-2">
                            <button
                                onClick={save}
                                className="btn-primary !py-2 !px-3 flex-1"
                                data-testid="pos-form-save"
                            >
                                Salva
                            </button>
                            <button
                                onClick={() => setEditing(null)}
                                className="btn-secondary !py-2 !px-3"
                                data-testid="pos-form-cancel"
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProfileTab() {
    const { user, setUser } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!name.trim()) {
            toast.error("Il nome non può essere vuoto");
            return;
        }
        setSaving(true);
        try {
            const r = await api.patch("/auth/me", { name: name.trim() });
            if (setUser) setUser(r.data);
            toast.success("Nome aggiornato");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-zinc-200 p-8 max-w-xl">
            <div className="overline mb-6">Il tuo profilo</div>
            <label className="block mb-4">
                <div className="overline mb-2">Email</div>
                <input className="inp bg-zinc-50 text-zinc-500" value={user?.email || ""} disabled />
                <div className="text-xs text-zinc-500 mt-1">L&apos;email non è modificabile.</div>
            </label>
            <label className="block mb-4">
                <div className="overline mb-2">Nome visualizzato</div>
                <input
                    className="inp"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="profile-name"
                />
            </label>
            <label className="block mb-6">
                <div className="overline mb-2">Ruolo</div>
                <input
                    className="inp bg-zinc-50 text-zinc-500"
                    value={user?.role || ""}
                    disabled
                />
            </label>
            <button
                onClick={save}
                disabled={saving}
                className="btn-primary"
                data-testid="profile-save"
            >
                {saving ? "Salvataggio…" : "Salva"}
            </button>

            <ChangePasswordSection />
        </div>
    );
}

function ChangePasswordSection() {
    const [oldPw, setOldPw] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (newPw.length < 6) {
            toast.error("La nuova password deve avere almeno 6 caratteri");
            return;
        }
        if (newPw !== confirmPw) {
            toast.error("Le due nuove password non coincidono");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                old_password: oldPw,
                new_password: newPw,
            });
            toast.success("Password aggiornata");
            setOldPw("");
            setNewPw("");
            setConfirmPw("");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="mt-10 pt-8 border-t border-zinc-200"
            data-testid="change-password-form"
        >
            <div className="overline mb-6">Cambia password</div>
            <label className="block mb-4">
                <div className="overline mb-2">Password attuale</div>
                <input
                    type="password"
                    className="inp"
                    value={oldPw}
                    onChange={(e) => setOldPw(e.target.value)}
                    autoComplete="current-password"
                    required
                    data-testid="change-pw-old"
                />
            </label>
            <label className="block mb-4">
                <div className="overline mb-2">Nuova password</div>
                <input
                    type="password"
                    className="inp"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    minLength={6}
                    autoComplete="new-password"
                    required
                    data-testid="change-pw-new"
                />
                <div className="text-xs text-zinc-500 mt-1">Min 6 caratteri</div>
            </label>
            <label className="block mb-6">
                <div className="overline mb-2">Conferma nuova password</div>
                <input
                    type="password"
                    className="inp"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    minLength={6}
                    autoComplete="new-password"
                    required
                    data-testid="change-pw-confirm"
                />
            </label>
            <button
                type="submit"
                disabled={saving || !oldPw || !newPw}
                className="btn-secondary"
                data-testid="change-pw-submit"
            >
                {saving ? "Salvataggio…" : "Aggiorna password"}
            </button>
        </form>
    );
}

export default function Settings() {
    const { user } = useAuth();
    const { tab } = useParams();
    const navigate = useNavigate();
    const activeTab =
        tab || (user?.role === "operator" ? "profile" : user?.role === "owner" ? "company" : "agency");

    const tabs = [
        ...(user?.role === "owner"
            ? [
                  {
                      key: "company",
                      label: "Azienda",
                      icon: <Buildings size={12} weight="bold" />,
                  },
              ]
            : []),
        {
            key: "agency",
            label: "Agenzia",
            icon: <Storefront size={12} weight="bold" />,
            roles: ["owner", "agency_manager"],
        },
        {
            key: "avanzate",
            label: "Avanzate",
            icon: <Wrench size={12} weight="bold" />,
            roles: ["owner"],
        },
        {
            key: "users",
            label: "Utenti",
            icon: <UsersIcon size={12} weight="bold" />,
            roles: ["owner", "agency_manager"],
        },
        {
            key: "profile",
            label: "Profilo",
            icon: <UserCircle size={12} weight="bold" />,
        },
    ].filter((t) => !t.roles || t.roles.includes(user?.role));

    return (
        <div data-testid="settings-page">
            <div className="mb-8">
                <div className="overline mb-2">Impostazioni</div>
                <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                    Configurazione
                </h1>
            </div>
            <div className="border-b border-zinc-200 mb-8 flex items-center overflow-x-auto">
                {tabs.map((t) => (
                    <TabButton
                        key={t.key}
                        active={activeTab === t.key}
                        onClick={() => navigate(`/settings/${t.key}`)}
                        icon={t.icon}
                        label={t.label}
                        testid={`settings-tab-${t.key}`}
                    />
                ))}
            </div>
            {activeTab === "company" && user?.role === "owner" && <CompanyTab />}
            {activeTab === "agency" && user?.role !== "operator" && <AgencyTab />}
            {(activeTab === "avanzate" || activeTab === "apertura") && user?.role === "owner" && <SettingsApertura />}
            {activeTab === "users" && user?.role !== "operator" && <UsersPage />}
            {activeTab === "profile" && <ProfileTab />}
            {user?.role === "operator" && ["company", "agency", "avanzate", "apertura", "users"].includes(activeTab) && (
                <div className="text-sm text-zinc-500 p-6 bg-zinc-50 border border-zinc-200">
                    Accesso non consentito al tuo ruolo. Puoi modificare solo il tuo profilo personale.
                </div>
            )}
        </div>
    );
}
